import { capitalizeFirstLetter } from '@/utils/commonHelpers';
import { fillIdPlaceholders } from '../../../helpers';
import type { CandidateSummary } from '../../../types';

// Selection drives the action; with nothing ticked, the whole list. Per-recipient
// email is safe at full-list scope because every draft opens for review.
export const fillTemplate = (
  template: string,
  selectedIds: string[],
  allIds: string[],
  appIds: string[] | undefined,
  nameById: Record<string, string>
): string => {
  const perRecipientEmail = template.includes('{recipients_phrase}');
  const ids = selectedIds.length > 0 ? selectedIds : allIds;
  const names = ids.map((id) => nameById[id] ?? '');
  // Keep app ids aligned to the chosen ids (parallel-by-index to allIds).
  let scopedAppIds = appIds;
  if (perRecipientEmail && appIds) {
    const idxById = new Map(allIds.map((id, i) => [id, i]));
    scopedAppIds = ids
      .map((id) => appIds[idxById.get(id) ?? -1])
      .filter((x): x is string => typeof x === 'string');
  }
  return fillIdPlaceholders(template, ids, scopedAppIds, names);
};

// Highlighted lead (first sentence) + gray remainder.
export const splitReason = (reason: string): { lead: string; rest: string } => {
  const match = reason.match(/^(.*?[.!?])\s+(.+)$/s);
  if (!match) return { lead: reason, rest: '' };
  return { lead: match[1], rest: match[2] };
};

// "Head of Product · Stripe · 7yrs"
export const buildSubheadline = (c: CandidateSummary): string => {
  const parts = [c.headline].filter(Boolean);
  if (typeof c.yearsExperience === 'number' && c.yearsExperience > 0) {
    parts.push(`${c.yearsExperience}yrs`);
  }
  return parts.join(' · ');
};

const ASPECT_LABEL: Record<string, string> = {
  note: 'Note',
  scorecard: 'Scorecard',
  transcript: 'Transcript',
  interview_summary: 'Interview',
  form: 'Form',
};

export const aspectLabel = (aspect: string): string =>
  ASPECT_LABEL[aspect] ?? (aspect ? capitalizeFirstLetter(aspect) : 'Evidence');
